import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface FormFieldProps {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  /**
   * The field must be filled. The label carries a red asterisk (hidden from screen readers, which hear
   * `aria-required` on the control instead), so every mandatory input looks the same wherever it appears
   * (business review 2026-09-23, plan §11.19).
   */
  required?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * `aria-required` is put on the control itself — never the HTML `required` attribute, which would make
 * the browser block a submit the page validates on its own, and would stop the estimator's autosave of
 * a half-finished record from looking like the normal thing it is.
 */
const markRequired = (children: ReactNode): ReactNode =>
  isValidElement(children) ? cloneElement(children as ReactElement<Record<string, unknown>>, { 'aria-required': true }) : children;

/** Label + control + error text. Every Sheet form is built out of these, so spacing stays uniform. */
export const FormField = ({ id, label, error, hint, required = false, children, className }: FormFieldProps) => (
  <div className={cn('space-y-1.5', className)}>
    <label
      htmlFor={id}
      className="block text-sm font-medium text-card-foreground"
    >
      {label}
      {required ? (
        <span
          aria-hidden
          className="ms-0.5 text-destructive"
        >
          *
        </span>
      ) : null}
    </label>
    {required ? markRequired(children) : children}
    {error ? <p className="text-xs text-destructive">{error}</p> : hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);

export interface FormSectionProps {
  title: string;
  children: ReactNode;
  /** One line under the title, for a section whose purpose is not obvious from its name. */
  description?: string;
}

/** A titled group of fields inside a Sheet. */
export const FormSection = ({ title, description, children }: FormSectionProps) => (
  <section className="space-y-3">
    <div className="space-y-0.5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
    </div>
    {children}
  </section>
);
